import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Lock, Users, FolderKanban, ListTodo, ClipboardList, CalendarDays, Clock, Receipt, FileText, DollarSign, Search, Loader2, ShieldCheck } from 'lucide-react';
import { format } from 'date-fns';
import api from '../lib/api';

export default function Admin() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [checking, setChecking] = useState(false);
  const [users, setUsers] = useState([]);
  const [overview, setOverview] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [togglingUser, setTogglingUser] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setChecking(true);
    try {
      await api.post('/admin/auth', { password });
      setAuthenticated(true);
      toast.success('Admin access granted');
    } catch {
      toast.error('Invalid admin password');
    } finally {
      setChecking(false);
    }
  };

  const fetchData = async (searchQuery = '') => {
    setLoading(true);
    try {
      const [usersRes, overviewRes] = await Promise.all([
        api.get('/admin/users', { params: { password, search: searchQuery || undefined } }),
        api.get('/admin/overview', { params: { password } })
      ]);
      setUsers(usersRes.data);
      setOverview(overviewRes.data);
    } catch {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authenticated) fetchData();
  }, [authenticated]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchData(search);
  };

  const toggleUser = async (userId) => {
    setTogglingUser(userId);
    try {
      const res = await api.put(`/admin/users/${userId}/toggle`, null, { params: { password } });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: res.data.is_active } : u));
      toast.success(`User ${res.data.is_active ? 'enabled' : 'disabled'}`);
    } catch {
      toast.error('Failed to toggle user');
    } finally {
      setTogglingUser(null);
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center p-4">
        <Card className="w-full max-w-sm border-0 shadow-lg">
          <CardHeader className="text-center pb-2">
            <div className="w-14 h-14 rounded-full bg-[#57534E]/10 flex items-center justify-center mx-auto mb-3">
              <ShieldCheck className="w-7 h-7 text-[#57534E]" />
            </div>
            <CardTitle className="text-xl font-medium">Admin Panel</CardTitle>
            <p className="text-sm text-[#78716C]">Enter admin password to continue</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label>Password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Admin password"
                  className="h-11"
                  data-testid="admin-password-input"
                />
              </div>
              <Button type="submit" className="w-full rounded-full bg-[#57534E] hover:bg-[#57534E]/90 h-11" disabled={checking} data-testid="admin-login-btn">
                {checking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                Access Admin Panel
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  const overviewItems = overview ? [
    { label: 'Users', value: overview.users, icon: Users, color: 'text-[#78866B]', bg: 'bg-[#78866B]/10' },
    { label: 'Clients', value: overview.clients, icon: Users, color: 'text-[#C98E75]', bg: 'bg-[#C98E75]/10' },
    { label: 'Projects', value: `${overview.projects.total} (${overview.projects.active} active)`, icon: FolderKanban, color: 'text-[#57534E]', bg: 'bg-[#57534E]/10' },
    { label: 'Tasks', value: `${overview.tasks.total} (${overview.tasks.todo} todo, ${overview.tasks.in_progress} in progress, ${overview.tasks.done} done)`, icon: ListTodo, color: 'text-[#78866B]', bg: 'bg-[#78866B]/10' },
    { label: 'Intakes', value: overview.intakes, icon: ClipboardList, color: 'text-yellow-600', bg: 'bg-yellow-100' },
    { label: 'Calendar Items', value: overview.calendar_items, icon: CalendarDays, color: 'text-blue-600', bg: 'bg-blue-100' },
    { label: 'Time Logs', value: overview.time_logs, icon: Clock, color: 'text-[#C98E75]', bg: 'bg-[#C98E75]/10' },
    { label: 'Expense Logs', value: overview.expense_logs, icon: Receipt, color: 'text-red-500', bg: 'bg-red-100' },
    { label: 'Invoices', value: `${overview.invoices.total} (${overview.invoices.draft} draft, ${overview.invoices.sent} sent, ${overview.invoices.paid} paid)`, icon: FileText, color: 'text-[#57534E]', bg: 'bg-[#57534E]/10' },
    { label: 'Transactions', value: overview.transactions, icon: DollarSign, color: 'text-[#78866B]', bg: 'bg-[#78866B]/10' },
  ] : [];

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6" data-testid="admin-panel">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-medium text-[#292524] tracking-tight">Admin Panel</h1>
            <p className="text-[#78716C] mt-1">System overview and user management</p>
          </div>
          <Button variant="outline" className="rounded-full" onClick={() => navigate('/')} data-testid="admin-back-btn">
            Back to App
          </Button>
        </div>

        {/* Data Overview */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-medium">Data Overview</CardTitle>
          </CardHeader>
          <CardContent>
            {loading && !overview ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#78716C]" /></div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4" data-testid="admin-overview">
                {overviewItems.map(item => {
                  const Icon = item.icon;
                  return (
                    <div key={item.label} className="p-3 rounded-xl bg-[#F5F5F4]/50">
                      <div className="flex items-center gap-2 mb-1">
                        <div className={`w-6 h-6 rounded-md ${item.bg} flex items-center justify-center`}>
                          <Icon className={`w-3.5 h-3.5 ${item.color}`} />
                        </div>
                        <span className="text-xs text-[#78716C] font-medium">{item.label}</span>
                      </div>
                      <p className="text-sm font-medium text-[#292524] pl-8">{item.value}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <CardTitle className="text-lg font-medium">Users ({users.length})</CardTitle>
              <form onSubmit={handleSearch} className="flex gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A8A29E]" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by email..."
                    className="h-9 pl-9 w-64"
                    data-testid="admin-search-input"
                  />
                </div>
                <Button type="submit" variant="outline" size="sm" className="h-9 rounded-full" data-testid="admin-search-btn">
                  Search
                </Button>
              </form>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-users-table">
                <thead>
                  <tr className="border-b border-[#E7E5E4]">
                    <th className="text-left py-3 px-4 font-medium text-[#78716C]">Email</th>
                    <th className="text-left py-3 px-4 font-medium text-[#78716C]">Name</th>
                    <th className="text-left py-3 px-4 font-medium text-[#78716C]">Created</th>
                    <th className="text-left py-3 px-4 font-medium text-[#78716C]">Last Login</th>
                    <th className="text-left py-3 px-4 font-medium text-[#78716C]">Active</th>
                    <th className="text-left py-3 px-4 font-medium text-[#78716C]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className="border-b border-[#F5F5F4] hover:bg-[#FAFAF9]" data-testid={`admin-user-${u.id}`}>
                      <td className="py-3 px-4 text-[#292524]">{u.email}</td>
                      <td className="py-3 px-4 text-[#292524]">{u.name}</td>
                      <td className="py-3 px-4 text-[#78716C]">
                        {u.created_at ? format(new Date(u.created_at), 'MMM d, yyyy') : '-'}
                      </td>
                      <td className="py-3 px-4 text-[#78716C]">
                        {u.last_login ? format(new Date(u.last_login), 'MMM d, yyyy HH:mm') : 'Never'}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${u.is_active !== false ? 'bg-[#78866B]/10 text-[#78866B]' : 'bg-red-100 text-red-600'}`}>
                          {u.is_active !== false ? 'Active' : 'Disabled'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs rounded-full"
                          onClick={() => toggleUser(u.id)}
                          disabled={togglingUser === u.id}
                          data-testid={`toggle-user-${u.id}`}
                        >
                          {togglingUser === u.id ? <Loader2 className="w-3 h-3 animate-spin" /> : (u.is_active !== false ? 'Disable' : 'Enable')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr><td colSpan={6} className="py-8 text-center text-[#A8A29E]">No users found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
